import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, ErrorHandler } from '@angular/core';
import { catchError, finalize, Observable, of, Subject, throwError } from 'rxjs';
import { UserService } from './user.services';
import { UserServiceDataService } from '../data-services/identity/user-service.data.service';
import { NotificationService } from './notification.service';
import { UserMenuService } from './user-menu.service';
import { ConnectionService } from './connection.service';

@Injectable()
export class HttpInterceptor implements HttpInterceptor {
  refreshTokenInProgress = false;

  constructor(
    private userService: UserService,
    private userDateService: UserServiceDataService,
    private notificationService: NotificationService,
    private userMenuService: UserMenuService,
    private connectionService: ConnectionService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    let interceptObservable = new Subject<HttpEvent<any>>();
    let modifiedRequest = this.normalizeRequestHeaders(request);

    if (
      !modifiedRequest.url.includes('RefreshToken') &&
      !modifiedRequest.url.includes('Login')
    ) {
      this.refreshToken(modifiedRequest, next).subscribe({
        next: (res) => {
          next
            .handle(res)
            .pipe(finalize(() => {}))
            .subscribe(
              (event: HttpEvent<any>) => {
                this.handleSuccessResponse(event, interceptObservable);
              },
              (error: any) => {
                return this.handleErrorResponse(
                  error,
                  interceptObservable,
                  next,
                  modifiedRequest
                ).subscribe((x) => {});
              }
            );
        },
        error: (error) => {
          // refresh token itself failed (e.g. 401) - stop hanging and surface the error
          interceptObservable.error(error);
          interceptObservable.complete();
        },
      });
    } else {
      next
        .handle(modifiedRequest)
        .pipe(finalize(() => {}))
        .subscribe(
          (event: HttpEvent<any>) => {
            this.handleSuccessResponse(event, interceptObservable);
          },
          (error: any) => {
            return this.handleErrorResponse(
              error,
              interceptObservable,
              next,
              modifiedRequest
            ).subscribe((x) => {});
          }
        );
    }
    return interceptObservable;
  }

  protected normalizeRequestHeaders(
    request: HttpRequest<any>
  ): HttpRequest<any> {
    let modifiedHeaders = new HttpHeaders();
    if (request.url.indexOf('i18n') === -1)
      modifiedHeaders = this.addAuthorizationHeaders(request.headers);

    return request.clone({
      headers: modifiedHeaders,
    });
  }

  protected addAuthorizationHeaders(headers: HttpHeaders): HttpHeaders {
    let authorizationHeaders = headers ? headers.getAll('Authorization') : null;
    if (!authorizationHeaders) {
      authorizationHeaders = [];
    }
    let token = this.userService.getToken();
    if (headers && token) {
      headers = headers.set('Authorization', 'Bearer ' + token);
    }
    return headers;
  }

  private itemExists<T>(items: T[], predicate: (item: T) => boolean): boolean {
    for (let i = 0; i < items.length; i++) {
      if (predicate(items[i])) {
        return true;
      }
    }

    return false;
  }

  private handleSuccessResponse(
    event: HttpEvent<any>,
    interceptObservable: Subject<HttpEvent<any>>
  ): void {
    let self = this;

    if (event instanceof HttpResponse) {
      if (
        event.body instanceof Blob &&
        event.body.type &&
        event.body.type.indexOf('application/json') >= 0
      ) {
        let clonedResponse = event.clone();

        this.blobToText(event.body).subscribe((json) => {
          const responseBody = json === 'null' ? {} : JSON.parse(json);

          let modifiedResponse = self.handleResponse(
            event.clone({
              body: responseBody,
            })
          );

          interceptObservable.next(
            modifiedResponse.clone({
              body: new Blob([JSON.stringify(modifiedResponse.body)], {
                type: 'application/json',
              }),
            })
          );

          interceptObservable.complete();
        });
      } else {
        interceptObservable.next(event);
        interceptObservable.complete();
      }
    } else {
      interceptObservable.next(event);
    }
  }

  blobToText(blob: any): Observable<string> {
    return new Observable<string>((observer: any) => {
      if (!blob) {
        observer.next('');
        observer.complete();
      } else {
        let reader = new FileReader();
        reader.onload = (event) => {
          observer.next((event.target as any).result);
          observer.complete();
        };
        reader.readAsText(blob);
      }
    });
  }

  private handleResponse(response: HttpResponse<any>): HttpResponse<any> {
    let ajaxResponse = this.getAjaxResponseOrNull(response);
    if (ajaxResponse == null) {
      return response;
    }
    return this.handleSREFResponse(response, ajaxResponse);
  }

  private getAjaxResponseOrNull(
    response: HttpResponse<any>
  ): IStatusResultResponse | null {
    if (!response || !response.headers) {
      return null;
    }

    let contentType = response.headers.get('Content-Type');
    if (!contentType) {
      return null;
    }

    if (contentType.indexOf('application/json') < 0) {
      return null;
    }

    let responseObj = JSON.parse(JSON.stringify(response.body));

    return responseObj as IStatusResultResponse;
  }

  private handleSREFResponse(
    response: HttpResponse<any>,
    ajaxResponse: IStatusResultResponse
  ): HttpResponse<any> {
    let newResponse: HttpResponse<any>;

    if (ajaxResponse.status === 'Success') {
      newResponse = response.clone({
        body: ajaxResponse.response,
      });
    } else {
      if (ajaxResponse.message)
        this.notificationService.error(ajaxResponse.message);

      newResponse = response.clone({
        body: ajaxResponse.response,
      });
    }

    return newResponse;
  }

  private handleErrorResponse(
    error: any,
    interceptObservable: Subject<HttpEvent<any>>,
    next: HttpHandler,
    req: HttpRequest<any>
  ): Observable<any> {
    let errorObservable = new Subject<any>();

    // Check if the error is caused by missing internet connection or network failure
    if (this.connectionService.isNetworkError(error)) {
      this.connectionService.notifyNoInternet();
      interceptObservable.error(error);
      interceptObservable.complete();
      return of({});
    }

    if (error instanceof HttpErrorResponse && error.status === 401) {
      this.userService.logout();
    }

    if (!(error.error instanceof Blob)) {
      interceptObservable.error(error);
      interceptObservable.complete();
      return of({});
    }

    this.blobToText(error.error).subscribe((json) => {
      const errorBody = json === '' || json === 'null' ? {} : JSON.parse(json);
      const errorResponse = new HttpResponse({
        headers: error.headers,
        status: error.status,
        body: errorBody,
      });

      let ajaxResponse = this.getAjaxResponseOrNull(errorResponse);

      if (ajaxResponse != null) {
        this.handleSREFResponse(errorResponse, ajaxResponse);
      } else {
        this.handleSREFErrorResponse(errorResponse);
      }

      errorObservable.complete();
      interceptObservable.error(error);
      interceptObservable.complete();
    });
    return errorObservable;
  }

  private handleSREFErrorResponse(response: any) {
    let newResponse: HttpResponse<any>;
    if (response) {
      if (response.status == 400) {
        if (response.body.status === 'Failure') {
          for (var key in response.body.response.errors) {
            if (response.body.response.errors.hasOwnProperty(key)) {
              alert(
                key.substring(key.indexOf('.')).replace('.', '') +
                  ' : ' +
                  response.body.response.errors[key]
              );
            }
          }
          newResponse = response.clone({
            body: response,
          });
        }
      }
    }
  }

  private refreshToken(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<any> {
    // if token is expired and session is exist (not login request)
    if (
      this.refreshTokenInProgress == false &&
      this.userService.isLoggedIn() &&
      new Date(parseInt(this.userService.getSessionExpiryDate()!)) <= new Date()
    ) {
      // handle requests that calles parallel with refresh token in progrss handle request

      // refresh token request
      this.refreshTokenInProgress = true;
      return new Observable((o) => {
        this.userDateService
          .refreshToken({
            userId: this.userService.getUserId()!,
            refreshToken: this.userService.getRefreshToken()!,
          })
          .subscribe(
            (res) => {
              this.userService.setToken(res.resource?.token!);
              this.userService.setRefreshToken(res.resource?.refreshToken!);
              this.userService.setSessionExpiryDate(
                res.resource?.expiryTokenInMinutes!
              );
              let modifiedRequest = this.normalizeRequestHeaders(req);
              this.refreshTokenInProgress = false;
              // return request with new token in header
              o.next(modifiedRequest);
              o.complete();
            },
            (err) => {
              this.refreshTokenInProgress = false;
              if (this.connectionService.isNetworkError(err)) {
                this.connectionService.notifyNoInternet();
              } else {
                this.userService.logout();
              }
              this.userService.logout();
              o.error(err);
            }
          );
      });
    } else {
      return new Observable((sup) => {
        sup.next(req);
        sup.complete();
      });
    }
  }
}

export interface IStatusResultResponse {
  status: string;
  exceptionMessage?: string;
  message?: string;
  response?: any;
}
